import { recogerDestino } from '../lib/destino.js';
import { Button, Field } from '@qr/ui';
import { CircleAlert, LoaderCircle, MailQuestion } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { messageFromError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/**
 * Vuelta del enlace mágico.
 *
 * Si el enlace se abre en el mismo dispositivo donde se pidió, el correo está
 * guardado y el acceso se completa solo. Si se abre en otro —el móvil, cuando
 * se pidió desde el ordenador—, hay que pedir el correo: es lo que impide que
 * alguien que intercepte el enlace lo use sin conocer la dirección.
 *
 * Los tres estados que caben aquí son muy distintos entre sí y conviene que lo
 * parezcan: uno es una espera de dos segundos, otro es una pregunta normal, y
 * el tercero es un callejón sin salida del que hay que ofrecer salida.
 */
export function CompleteSignIn() {
  const { completeSignIn } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState<'working' | 'needs-email' | 'error'>('working');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // En modo estricto de React los efectos se ejecutan dos veces en desarrollo.
  // El enlace mágico solo sirve una vez, así que el segundo intento fallaría
  // con un error confuso si no se protegiera.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    completeSignIn()
      // A lo que venia a hacer, si lo dijo antes de entrar: es el final del
      // camino que empieza eligiendo un plan en la portada.
      .then(() => navigate(recogerDestino() ?? '/', { replace: true }))
      .catch((caught: unknown) => {
        const message = messageFromError(caught);
        // Falta el correo: es el caso normal al abrir en otro dispositivo, no
        // un fallo.
        if (message.includes('correo')) {
          setStatus('needs-email');
          return;
        }
        setError(message);
        setStatus('error');
      });
  }, [completeSignIn, navigate]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await completeSignIn(email);
      navigate(recogerDestino() ?? '/', { replace: true });
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-ground px-5 py-10">
      <div className="flex w-full max-w-[24rem] flex-col gap-5">
        {status === 'working' ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <LoaderCircle
              size={26}
              className="animate-spin text-brand"
              aria-hidden
            />
            <p className="m-0 text-[14.5px] text-ink-2" role="status">
              Comprobando el enlace de acceso...
            </p>
          </div>
        ) : null}

        {status === 'needs-email' ? (
          <>
            <header className="flex flex-col gap-3">
              <span
                aria-hidden
                className="grid size-12 place-items-center rounded-xl bg-brand-soft text-brand-deep"
              >
                <MailQuestion size={23} strokeWidth={1.8} />
              </span>
              <h1 className="m-0 text-2xl font-bold tracking-tight">
                Confirma tu correo
              </h1>
              <p className="m-0 text-[14.5px] leading-relaxed text-ink-2">
                Has abierto el enlace en un dispositivo distinto del que lo pidió.
                Escribe la dirección a la que lo enviamos y entras.
              </p>
            </header>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
              <Field
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                inputMode="email"
                autoFocus
                required
                placeholder="tu@empresa.com"
                error={error}
              />
              <Button type="submit" loading={submitting} disabled={email.trim() === ''}>
                Entrar
              </Button>
            </form>

            <p className="m-0 text-[12.5px] leading-relaxed text-ink-3">
              Te lo pedimos por seguridad: sin la dirección, un enlace interceptado no
              sirve de nada.
            </p>
          </>
        ) : null}

        {status === 'error' ? (
          <>
            <header className="flex flex-col gap-3">
              <span
                aria-hidden
                className="grid size-12 place-items-center rounded-xl bg-bad-soft text-bad"
              >
                <CircleAlert size={23} strokeWidth={1.8} />
              </span>
              <h1 className="m-0 text-2xl font-bold tracking-tight">
                Este enlace ya no sirve
              </h1>
              <p className="m-0 text-[14.5px] leading-relaxed text-ink-2">
                {/* Casi siempre es una de dos: caducó, o ya se usó una vez.
                    Ninguna de las dos es culpa de nadie ni tiene arreglo aquí,
                    así que lo único útil es el botón de debajo. */}
                Los enlaces de acceso caducan pronto y solo valen para una entrada.
                Pide uno nuevo y listo.
              </p>
            </header>

            {error ? (
              <p className="m-0 rounded-DEFAULT border border-line bg-surface px-3.5 py-2.5 font-mono text-[12px] break-words text-ink-3">
                {error}
              </p>
            ) : null}

            <div>
              <Button onClick={() => navigate('/acceso', { replace: true })}>
                Pedir un enlace nuevo
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </main>
  );
}
